// Third-party
import { uniqueId } from 'lodash';
import React, { MutableRefObject, useState, useRef, useCallback, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import TableMenuToggle from './TableMenuToggle';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useThemeVersion from '@/hooks/useThemeVersion';
import classNames from 'classnames';
import { formatMessage } from '@/utils/i18n';

type Props = {
  options: React.ReactElement[];
  disabled?: boolean;
  alignRight?: true;
  scrollbarContainerRef?: HTMLElement;
  hideOnScrollAway?: boolean;
  psbRef?: MutableRefObject<PerfectScrollbar | null> | null;
  dataTestId?: string;
  ariaLabel?: string;
};

const GenericMenuToggle = (props: Props) => {
  const { alignRight, dataTestId, disabled, options = [], hideOnScrollAway, scrollbarContainerRef, ...rest } = props;

  const [showDropdown, setShowDropdown] = useState(false);
  const [, setIsPositioned] = useState(false);
  const id = useRef(uniqueId('menu-toggle-')).current;
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const trackDropdownPosition = useCallback(() => {
    const dropdownRefElement = dropdownRef.current;
    if (!dropdownRefElement || !showDropdown || !hideOnScrollAway) return;

    const containerOffsetLeft = scrollbarContainerRef?.offsetLeft || 0;
    const dropdownRefOffsetLeft = dropdownRefElement.getBoundingClientRect().left;

    if (dropdownRefOffsetLeft && dropdownRefOffsetLeft < containerOffsetLeft) {
      setShowDropdown(false);
    }
  }, [showDropdown, hideOnScrollAway, scrollbarContainerRef]);

  // Handle positioning after dropdown shows
  useEffect(() => {
    const isNotEmbeddedInIframe = window.self === window.top;
    if (showDropdown && isNotEmbeddedInIframe) {
      // Dropdown will render on next frame
      const rafId = requestAnimationFrame(() => {
        // Now the dropdown is definitely in the DOM and positioned
        setIsPositioned(true); // Trigger any positioning-dependent effects
      });
      return () => cancelAnimationFrame(rafId);
    }
    setIsPositioned(false); // Reset immediately when hiding
  }, [showDropdown]);

  useEffect(() => {
    if (hideOnScrollAway && scrollbarContainerRef && showDropdown) {
      scrollbarContainerRef.addEventListener('scroll', trackDropdownPosition);

      return () => {
        scrollbarContainerRef.removeEventListener('scroll', trackDropdownPosition);
      };
    }
  }, [hideOnScrollAway, scrollbarContainerRef, showDropdown, trackDropdownPosition]);

  return (
    <Dropdown
      data-testid={dataTestId ?? 'menu-toggle'}
      ref={dropdownRef}
      show={showDropdown}
      className="menu-toggle-v2"
      onToggle={setShowDropdown}
      role="menutoggle"
    >
      <Dropdown.Toggle id={id} disabled={disabled} aria-label={props.ariaLabel}>
        <i className="icon-ds-more-vertical" />
      </Dropdown.Toggle>

      <Dropdown.Menu as="ul" role="menu" {...(alignRight && { align: 'end' })}>
        {options.map(option => (
          <li key={option.key} role="menuitem">
            {option}
          </li>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const MenuToggle = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  const ariaLabel = props.ariaLabel || formatMessage('Common.MenuToggle');
  return isLightVersion && props.psbRef ? (
    <TableMenuToggle psbRef={props.psbRef} ariaLabel={ariaLabel} {...props} />
  ) : (
    <GenericMenuToggle ariaLabel={ariaLabel} {...props} />
  );
};

export default MenuToggle;
